import { useMemo, useRef, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { AppShell } from "@/components/app/AppShell";
import { Btn } from "@/components/ui/Btn/Btn";
import { Icon } from "@/components/ui/Icon/Icon";
import { Modal } from "@/components/ui/Modal/Modal";
import { PlatformBadge, toPlatformKey } from "@/components/ui/PlatformBadge/PlatformBadge";
import { Segmented } from "@/components/ui/Segmented/Segmented";
import { useAuth } from "@/lib/AuthContext";
import { useDrafts, useScheduleDraft, useUnscheduleDraft } from "@/lib/queries";
import type { DraftRecord } from "@notamen/core/db";
import styles from "./Calendar.module.css";

type View = "month" | "week" | "list";
/** A scheduled draft is guaranteed to carry a scheduledFor timestamp. */
type ScheduledDraft = DraftRecord & { scheduledFor: string };

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const pad = (n: number) => String(n).padStart(2, "0");
const dayKey = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const addDays = (d: Date, n: number) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const addMonths = (d: Date, n: number) => { const x = new Date(d); x.setMonth(x.getMonth() + n); return x; };
const startOfWeek = (d: Date) => addDays(d, -d.getDay());
const sameDay = (a: Date, b: Date) => dayKey(a) === dayKey(b);

/** Local "YYYY-MM-DDTHH:mm" for a datetime-local input. */
const toLocalInput = (iso: string) => {
  const d = new Date(iso);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
/** A datetime-local value (interpreted local) → ISO (UTC). */
const fromLocalInput = (v: string) => new Date(v).toISOString();
const fmtTime = (iso: string) =>
  new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
const fmtDayLong = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });
const previewText = (d: DraftRecord) =>
  d.draft.variants[0]?.hook || d.draft.variants[0]?.body || "Scheduled post";

export function Calendar() {
  const { ws, brand } = useParams<{ ws: string; brand: string }>();
  const auth = useAuth();
  const draftsQuery = useDrafts(ws, brand);
  const reschedule = useScheduleDraft(ws, brand);
  const unschedule = useUnscheduleDraft(ws, brand);

  const [view, setView] = useState<View>("month");
  const [cursor, setCursor] = useState(() => new Date());
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const draggedId = useRef<string | null>(null);

  const scheduled = useMemo<ScheduledDraft[]>(
    () =>
      (draftsQuery.data?.drafts ?? [])
        .filter((d): d is ScheduledDraft =>
          d.status === "scheduled" && typeof d.scheduledFor === "string")
        .sort((a, b) => a.scheduledFor.localeCompare(b.scheduledFor)),
    [draftsQuery.data],
  );

  const byDay = useMemo(() => {
    const map = new Map<string, ScheduledDraft[]>();
    for (const d of scheduled) {
      const k = dayKey(new Date(d.scheduledFor));
      const arr = map.get(k);
      if (arr) arr.push(d);
      else map.set(k, [d]);
    }
    return map;
  }, [scheduled]);

  if (auth.status === "anonymous") {
    return <Navigate to={`/signin?next=/w/${ws}/b/${brand}/calendar`} replace />;
  }

  function moveDraggedTo(targetDay: Date) {
    const id = draggedId.current;
    draggedId.current = null;
    if (!id) return;
    const post = scheduled.find((d) => d.draft.id === id);
    if (!post) return;
    const orig = new Date(post.scheduledFor);
    const next = new Date(targetDay);
    next.setHours(orig.getHours(), orig.getMinutes(), 0, 0);
    if (dayKey(next) === dayKey(orig)) return; // dropped on its own day — no change
    reschedule.mutate({ draftId: id, scheduledFor: next.toISOString() });
  }

  const dayPosts = selectedDay ? byDay.get(selectedDay) ?? [] : [];
  const actionError = (reschedule.error ?? unschedule.error)?.message;

  return (
    <AppShell currentWorkspaceId={ws}>
      <div className={styles.page}>
        <header className={styles.head}>
          <div className={styles.headLeft}>
            <Btn href={`/w/${ws}/b/${brand}`} variant="ghost" size="sm" iconLeft={<Icon name="arrow-left" size={13} />}>
              Back to brand
            </Btn>
            <h1 className={styles.title}>Calendar</h1>
          </div>
          <Segmented
            value={view}
            onChange={(k) => setView(k as View)}
            items={[
              { key: "month", label: "Month" },
              { key: "week", label: "Week" },
              { key: "list", label: "List" },
            ]}
          />
        </header>

        {draftsQuery.isLoading && <p className={styles.muted}>Loading schedule…</p>}
        {draftsQuery.error && (
          <p className={styles.err}>Couldn’t load drafts: {draftsQuery.error.message}</p>
        )}
        {/* Surface reschedule/cancel failures (e.g. a drag-to-reschedule) even
            when the day drawer is closed; the drawer shows its own copy. */}
        {actionError && selectedDay === null && <p className={styles.err}>{actionError}</p>}

        {!draftsQuery.isLoading && !draftsQuery.error && scheduled.length === 0 && (
          <div className={styles.empty}>
            <Icon name="calendar" size={28} />
            <p>No scheduled posts yet. Approve a draft and schedule it to see it here.</p>
            <Btn href={`/w/${ws}/b/${brand}/queue`} variant="secondary" size="sm">
              Go to the approval queue
            </Btn>
          </div>
        )}

        {scheduled.length > 0 && view === "month" && (
          <MonthView cursor={cursor} setCursor={setCursor} byDay={byDay} onPick={setSelectedDay} />
        )}
        {scheduled.length > 0 && view === "week" && (
          <WeekView
            cursor={cursor}
            setCursor={setCursor}
            byDay={byDay}
            onPick={setSelectedDay}
            onDragStartPost={(id) => { draggedId.current = id; }}
            onDropDay={moveDraggedTo}
          />
        )}
        {scheduled.length > 0 && view === "list" && (
          <ListView scheduled={scheduled} onPick={setSelectedDay} />
        )}
      </div>

      <Modal
        open={selectedDay !== null}
        title={dayPosts[0] ? fmtDayLong(dayPosts[0].scheduledFor) : "Scheduled posts"}
        onClose={() => setSelectedDay(null)}
        footer={<Btn variant="ghost" onClick={() => setSelectedDay(null)}>Close</Btn>}
      >
        {dayPosts.length === 0 ? (
          <p className={styles.muted}>Nothing scheduled this day.</p>
        ) : (
          <div className={styles.drawerList}>
            {dayPosts.map((post) => (
              <DrawerPost
                key={post.draft.id}
                post={post}
                queueHref={`/w/${ws}/b/${brand}/queue`}
                rescheduling={reschedule.isPending}
                cancelling={unschedule.isPending}
                error={actionError}
                onReschedule={(scheduledFor) => reschedule.mutate({ draftId: post.draft.id, scheduledFor })}
                onCancel={() => unschedule.mutate({ draftId: post.draft.id })}
              />
            ))}
          </div>
        )}
      </Modal>
    </AppShell>
  );
}

interface ViewProps {
  cursor: Date;
  setCursor: (d: Date) => void;
  byDay: Map<string, ScheduledDraft[]>;
  onPick: (dayKey: string) => void;
}

function MonthView({ cursor, setCursor, byDay, onPick }: ViewProps) {
  const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const gridStart = startOfWeek(first);
  const cells = Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
  const today = new Date();

  return (
    <div className={styles.calCard}>
      <div className={styles.navBar}>
        <Btn variant="ghost" size="sm" iconLeft={<Icon name="arrow-left" size={13} />} onClick={() => setCursor(addMonths(cursor, -1))}>
          Prev
        </Btn>
        <span className={styles.navLabel}>{MONTHS[cursor.getMonth()]} {cursor.getFullYear()}</span>
        <Btn variant="ghost" size="sm" iconRight={<Icon name="arrow-right" size={13} />} onClick={() => setCursor(addMonths(cursor, 1))}>
          Next
        </Btn>
      </div>
      <div className={styles.weekHead}>
        {WEEKDAYS.map((w) => <span key={w} className={styles.weekHeadCell}>{w}</span>)}
      </div>
      <div className={styles.monthGrid}>
        {cells.map((d) => {
          const posts = byDay.get(dayKey(d)) ?? [];
          const outside = d.getMonth() !== cursor.getMonth();
          const clickable = posts.length > 0;
          return (
            <div
              key={dayKey(d)}
              className={`${styles.dayCell} ${outside ? styles.outside : ""} ${sameDay(d, today) ? styles.today : ""} ${clickable ? styles.clickable : ""}`}
              {...(clickable
                ? {
                    role: "button",
                    tabIndex: 0,
                    onClick: () => onPick(dayKey(d)),
                    onKeyDown: (e: React.KeyboardEvent) => {
                      if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onPick(dayKey(d)); }
                    },
                  }
                : {})}
            >
              <span className={styles.dayNum}>{d.getDate()}</span>
              {clickable && (
                <span className={styles.dots}>
                  {posts.slice(0, 3).map((p) => (
                    <span key={p.draft.id} className={styles.dot} style={{ background: `var(--platform-${toPlatformKey(p.platform)})` }} />
                  ))}
                  {posts.length > 3 && <span className={styles.more}>+{posts.length - 3}</span>}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface WeekViewProps extends ViewProps {
  onDragStartPost: (draftId: string) => void;
  onDropDay: (day: Date) => void;
}

function WeekView({ cursor, setCursor, byDay, onPick, onDragStartPost, onDropDay }: WeekViewProps) {
  const weekStart = startOfWeek(cursor);
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const today = new Date();

  return (
    <div className={styles.calCard}>
      <div className={styles.navBar}>
        <Btn variant="ghost" size="sm" iconLeft={<Icon name="arrow-left" size={13} />} onClick={() => setCursor(addDays(cursor, -7))}>
          Prev
        </Btn>
        <span className={styles.navLabel}>
          Week of {weekStart.toLocaleDateString(undefined, { month: "short", day: "numeric" })}
        </span>
        <Btn variant="ghost" size="sm" iconRight={<Icon name="arrow-right" size={13} />} onClick={() => setCursor(addDays(cursor, 7))}>
          Next
        </Btn>
      </div>
      <p className={styles.hint}>Drag a post onto another day to reschedule it.</p>
      <div className={styles.weekGrid}>
        {days.map((d) => {
          const posts = byDay.get(dayKey(d)) ?? [];
          return (
            <div
              key={dayKey(d)}
              className={`${styles.weekCol} ${sameDay(d, today) ? styles.todayCol : ""}`}
              onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; }}
              onDrop={(e) => { e.preventDefault(); onDropDay(d); }}
            >
              <div className={styles.weekColHead}>
                <span className={styles.weekColDow}>{WEEKDAYS[d.getDay()]}</span>
                <span className={styles.weekColDate}>{d.getDate()}</span>
              </div>
              <div className={styles.weekColBody}>
                {posts.map((p) => (
                  <div
                    key={p.draft.id}
                    className={styles.block}
                    draggable
                    onDragStart={(e) => {
                      onDragStartPost(p.draft.id);
                      e.dataTransfer.effectAllowed = "move";
                      e.dataTransfer.setData("text/plain", p.draft.id);
                    }}
                    role="button"
                    tabIndex={0}
                    onClick={() => onPick(dayKey(d))}
                    onKeyDown={(e) => { if (e.key === "Enter") onPick(dayKey(d)); }}
                  >
                    <span className={styles.blockTime}>{fmtTime(p.scheduledFor)}</span>
                    <span className={styles.dot} style={{ background: `var(--platform-${toPlatformKey(p.platform)})` }} />
                    <span className={styles.blockText}>{previewText(p)}</span>
                  </div>
                ))}
                {posts.length === 0 && <span className={styles.weekColEmpty}>—</span>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ListView({ scheduled, onPick }: { scheduled: ScheduledDraft[]; onPick: (dayKey: string) => void }) {
  // scheduled is already sorted by time, so Map insertion order is chronological.
  const grouped = new Map<string, { key: string; date: Date; posts: ScheduledDraft[] }>();
  for (const p of scheduled) {
    const d = new Date(p.scheduledFor);
    const k = dayKey(d);
    const g = grouped.get(k);
    if (g) g.posts.push(p);
    else grouped.set(k, { key: k, date: d, posts: [p] });
  }
  const groups = Array.from(grouped.values());

  return (
    <div className={styles.list}>
      {groups.map((g) => (
        <div key={g.key} className={styles.listGroup}>
          <h3 className={styles.listDay}>
            {g.date.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}
          </h3>
          {g.posts.map((p) => (
            <button key={p.draft.id} type="button" className={styles.listRow} onClick={() => onPick(g.key)}>
              <span className={styles.listTime}>{fmtTime(p.scheduledFor)}</span>
              <PlatformBadge platform={toPlatformKey(p.platform)} size={18} />
              <span className={styles.listText}>{previewText(p)}</span>
              <Icon name="arrow-right" size={13} />
            </button>
          ))}
        </div>
      ))}
    </div>
  );
}

interface DrawerPostProps {
  post: ScheduledDraft;
  queueHref: string;
  rescheduling: boolean;
  cancelling: boolean;
  error?: string | undefined;
  onReschedule: (scheduledFor: string) => void;
  onCancel: () => void;
}

function DrawerPost({ post, queueHref, rescheduling, cancelling, error, onReschedule, onCancel }: DrawerPostProps) {
  const v = post.draft.variants[0];
  const [when, setWhen] = useState(() => toLocalInput(post.scheduledFor));

  return (
    <div className={styles.drawerPost}>
      <div className={styles.drawerPostHead}>
        <PlatformBadge platform={toPlatformKey(post.platform)} size={20} />
        <span className={styles.drawerTime}>{fmtTime(post.scheduledFor)}</span>
      </div>
      {v?.hook && <p className={styles.drawerHook}>{v.hook}</p>}
      {v?.body && <p className={styles.drawerBody}>{v.body}</p>}
      <div className={styles.drawerControls}>
        <label className={styles.dtField}>
          <span className={styles.dtLabel}>Reschedule</span>
          <input
            type="datetime-local"
            className={styles.dtInput}
            value={when}
            onChange={(e) => setWhen(e.target.value)}
          />
        </label>
        <div className={styles.drawerButtons}>
          <Btn size="sm" variant="secondary" loading={rescheduling} iconLeft={<Icon name="calendar" size={13} />} onClick={() => onReschedule(fromLocalInput(when))}>
            Save time
          </Btn>
          <Btn size="sm" variant="ghost" loading={cancelling} iconLeft={<Icon name="x" size={13} />} onClick={onCancel}>
            Cancel schedule
          </Btn>
          <Btn size="sm" variant="ghost" href={queueHref} iconLeft={<Icon name="check-circle" size={13} />}>
            Open in queue
          </Btn>
        </div>
      </div>
      {error && <p className={styles.err}>{error}</p>}
    </div>
  );
}
